// Helper class for roller values
export class InputValue {
  private constructor(
    public numeric: number,
    public label: string
  ) { }

  // build an InputValue with an explicit pre-formatted label (e.g. "2:15"
   // for a duration roller) while keeping a numeric key for lookups
  static fromLabel(numeric: number, label: string): InputValue {
    return new InputValue(numeric, label);
  }

  static fromNumber(numeric: number, decimalPlaces: number, decimalSeparator: string): InputValue {
    if (!isFinite(numeric)) {
      return new InputValue(undefined, '--');
    }

    let label = numeric.toFixed(decimalPlaces);

    // Replace decimal separator if needed
    if (decimalSeparator !== '.') {
      label = label.replace('.', decimalSeparator);
    }

    return new InputValue(numeric, label);
  }

  static findClosest(targetValue: number, values: InputValue[]): InputValue {
    if (values.length === 0) {
      // Return a safe default - this shouldn't happen in practice
      // since we only call findClosest when we have values
      return new InputValue(undefined, '--');
    }

    let closest = values[0];
    let minDiff = Math.abs(values[0].numeric - targetValue);

    for (const val of values) {
      const diff = Math.abs(val.numeric - targetValue);
      if (diff < minDiff) {
        minDiff = diff;
        closest = val;
      }
    }

    return closest;
  }

  static findByValue(targetValue: number, values: InputValue[]): InputValue | null {
    // Use epsilon comparison for floating point equality
    const epsilon = 0.0001;
    return values.find(v => Math.abs(v.numeric - targetValue) < epsilon) || null;
  }

  static findIndexByNumeric(targetValue: number, values: InputValue[]): number {
    // First try exact match with epsilon
    const epsilon = 0.0001;
    const exactIndex = values.findIndex(v => Math.abs(v.numeric - targetValue) < epsilon);
    if (exactIndex >= 0) {
      return exactIndex;
    }

    // If no exact match, find closest value
    if (values.length === 0) {
      return -1;
    }

    let closestIndex = 0;
    let minDiff = Math.abs(values[0].numeric - targetValue);

    for (let i = 1; i < values.length; i++) {
      const diff = Math.abs(values[i].numeric - targetValue);
      if (diff < minDiff) {
        minDiff = diff;
        closestIndex = i;
      }
    }

    return closestIndex;
  }
}